import { CheckCircleOutlined } from '@ant-design/icons';
import { ProductDoc } from '@gooduncles/gu-app-schema';
import { Button, Empty, InputRef, Modal, message } from 'antd';
import Search from 'antd/es/input/Search';
import { debounce } from 'lodash-es';
import { FC, cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import useAlgoliaSearch from 'src/hooks/swr/useAlgoliaSearch';
import { getProductsByIds } from 'src/utils/firebase/firebase-short-cut';

import ProductListItem from 'src/atomic-components/atoms/ListItem/ProductListItem';
import DraggableFavoriteItem from 'src/atomic-components/organisms/Modals/FavoriteProductRow/DraggableFavoriteItem';
import FavoriteProductRow from 'src/atomic-components/organisms/Modals/FavoriteProductRow/FavoriteProductRow';

const pageSize = 20;

interface AddProductToListModalProps {
  openButton: React.ReactElement;
  currentProductIds: string[];
  /** 기존 즉시 모드: 검색 결과 클릭 시 즉시 호출된다. */
  onAdd?: (productId: string) => Promise<void | string>;
  onRemove?: (productId: string) => Promise<void | string>;
  /** 'staged'면 변경사항을 누적 후 저장 버튼 클릭 시 일괄 반영한다. */
  mode?: 'immediate' | 'staged';
  onSave?: (ids: string[]) => Promise<void>;
  title?: string;
  /** staged 모드에서 검색 위에 현재 즐겨찾기 목록을 보여줄지 여부. */
  showCurrentList?: boolean;
  /** 현재 목록을 드래그해서 순서를 바꿀 수 있게 할지 여부. */
  enableReorder?: boolean;
}

const moveInArray = <T,>(arr: T[], from: number, to: number): T[] => {
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const AddProductToListModal: FC<AddProductToListModalProps> = ({
  openButton,
  currentProductIds,
  onAdd,
  onRemove,
  mode = 'immediate',
  onSave,
  title = '상품 추가',
  showCurrentList = false,
  enableReorder = false,
}) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [keyword, setKeyword] = useState('');
  const [hitsPerPage, setHitsPerPage] = useState(pageSize);
  const savedScrollPosition = useRef<number>(0);
  const shouldRestoreScroll = useRef<boolean>(false);

  const [draftIds, setDraftIds] = useState<string[]>(currentProductIds);
  const [productCache, setProductCache] = useState<Map<string, ProductDoc | null>>(new Map());
  const [saving, setSaving] = useState(false);

  const handleInputChangeWithDebounce = useMemo(
    () =>
      debounce((e) => {
        setKeyword(e.target.value);
        setHitsPerPage(pageSize);
      }, 300),
    [setKeyword],
  );
  const { hits, total, loading, error } = useAlgoliaSearch(keyword, hitsPerPage);

  const handleOpen = () => setOpen(true);
  const handleClose = useCallback(() => {
    if (saving) return;
    setOpen(false);
  }, [saving]);

  const showMore = () => {
    savedScrollPosition.current = scrollContainerRef.current?.scrollTop || 0;
    shouldRestoreScroll.current = true;
    setHitsPerPage((prev) => prev + pageSize);
  };

  useEffect(() => {
    if (!loading && shouldRestoreScroll.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedScrollPosition.current;
      shouldRestoreScroll.current = false;
    }
  }, [loading, hits]);

  useEffect(() => {
    if (error) {
      message.error(error.message);
    }
  }, [error]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [inputRef, open]);

  // staged 모드: 모달 열릴 때마다 draftIds를 원본 기준으로 재초기화
  useEffect(() => {
    if (mode === 'staged' && open) {
      setDraftIds(currentProductIds);
    }
  }, [mode, open, currentProductIds]);

  // staged 모드: 현재 목록 표시용 ProductDoc을 cache에 없는 ID만 fetch
  useEffect(() => {
    if (mode !== 'staged' || !open || !showCurrentList) return;
    const missing = draftIds.filter((id) => !productCache.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    getProductsByIds(missing).then((docs) => {
      if (cancelled) return;
      setProductCache((prev) => {
        const next = new Map(prev);
        missing.forEach((id, i) => next.set(id, docs[i]));
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [mode, open, showCurrentList, draftIds, productCache]);

  const effectiveIds = mode === 'staged' ? draftIds : currentProductIds;

  const handleToggleFromSearch = async (productId: string) => {
    if (mode === 'staged') {
      setDraftIds((prev) => (prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]));
    } else if (effectiveIds.includes(productId)) {
      if (onRemove) await onRemove(productId);
    } else {
      if (onAdd) await onAdd(productId);
    }
  };

  const handleRemoveFromDraft = (productId: string) => {
    setDraftIds((prev) => prev.filter((id) => id !== productId));
  };

  const handleMoveItem = useCallback((dragIndex: number, hoverIndex: number) => {
    setDraftIds((prev) => moveInArray(prev, dragIndex, hoverIndex));
  }, []);

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(draftIds);
      setOpen(false);
    } catch (e: any) {
      message.error(e?.message ?? '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const isStaged = mode === 'staged';
  const showTwoColumn = isStaged && showCurrentList;
  const beforeCount = currentProductIds.length;
  const afterCount = draftIds.length;
  const countChanged = beforeCount !== afterCount;

  const currentListContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, overflowY: 'auto', minHeight: 0 }}>
      {draftIds.length === 0 ? (
        <Empty description='추가된 상품이 없습니다' image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : enableReorder ? (
        draftIds.map((id, index) => (
          <DraggableFavoriteItem
            key={id}
            index={index}
            productId={id}
            product={productCache.get(id) ?? null}
            moveItem={handleMoveItem}
            onRemove={() => handleRemoveFromDraft(id)}
          />
        ))
      ) : (
        draftIds.map((id) => (
          <FavoriteProductRow
            key={id}
            productId={id}
            product={productCache.get(id) ?? null}
            onRemove={() => handleRemoveFromDraft(id)}
          />
        ))
      )}
    </div>
  );

  const searchPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0 }}>
      <Search ref={inputRef} size='large' loading={loading} onChange={handleInputChangeWithDebounce} />
      <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {hits?.map((hit) => (
          <ProductListItem
            key={hit.objectID}
            product={hit}
            rightElement={
              effectiveIds.includes(hit.productId) ? (
                <Button
                  type='text'
                  icon={<CheckCircleOutlined style={{ color: 'var(--green400)' }} />}
                  onClick={() => handleToggleFromSearch(hit.productId)}
                />
              ) : (
                <Button
                  type='text'
                  icon={<CheckCircleOutlined style={{ color: 'var(--gray300)' }} />}
                  onClick={() => handleToggleFromSearch(hit.productId)}
                />
              )
            }
          />
        ))}
        {total && total > hitsPerPage ? (
          <Button onClick={showMore} block loading={loading}>
            더보기
          </Button>
        ) : null}
      </div>
    </div>
  );

  const currentListPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minHeight: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--gray700)' }}>
        현재 목록{' '}
        <span
          style={{ color: countChanged ? 'var(--blue500)' : undefined, fontWeight: countChanged ? 600 : undefined }}
        >
          ({beforeCount}개 → {afterCount}개)
        </span>
      </div>
      {enableReorder ? <DndProvider backend={HTML5Backend}>{currentListContent}</DndProvider> : currentListContent}
    </div>
  );

  return (
    <>
      {cloneElement(openButton, { onClick: handleOpen })}
      <Modal
        open={open}
        onCancel={handleClose}
        maskClosable={!saving}
        closable={!saving}
        footer={
          isStaged
            ? [
                <Button key='cancel' onClick={handleClose} disabled={saving}>
                  취소
                </Button>,
                <Button key='save' type='primary' loading={saving} onClick={handleSave}>
                  저장
                </Button>,
              ]
            : null
        }
        width={showTwoColumn ? 880 : 420}
        styles={{
          body: { height: '70vh', display: 'flex', flexDirection: 'column' },
        }}
        title={title}
      >
        {showTwoColumn ? (
          <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
            {searchPanel}
            {currentListPanel}
          </div>
        ) : (
          searchPanel
        )}
      </Modal>
    </>
  );
};

export default AddProductToListModal;
